import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, MessageSquare, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { MentionInput, MentionText, mentionsIn } from "@/components/MentionInput";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWorld } from "@/context/WorldContext";
import { ago, isReviewer } from "@/lib/perms";

const Composer = ({ onSubmit, placeholder, initial = "", testId, onCancel, people }) => {
  const [v, setV] = useState(initial);
  const send = async (e) => { e.preventDefault(); const t = v.trim(); if (t && (await onSubmit(t, mentionsIn(t, people))) !== false) setV(""); };
  return (
    <form onSubmit={send} className="space-y-2">
      <MentionInput value={v} onChange={setV} people={people} placeholder={placeholder} testId={testId} />
      <div className="flex gap-2">
        <button className="rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent text-xs font-semibold px-4 py-1.5" data-testid={`${testId}-submit`}>Post</button>
        {onCancel && <button type="button" onClick={onCancel} className="text-xs text-slate-400">Cancel</button>}
      </div>
    </form>
  );
};

const Comment = ({ c, me, mod, act, replies, onReply, people }) => {
  const [editing, setEditing] = useState(false);
  return (
    <div className="flex gap-3" data-testid={`comment-${c.id}`}>
      <span className="h-8 w-8 shrink-0 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-200 text-xs font-semibold flex items-center justify-center overflow-hidden">
        {c.author_picture ? <img src={c.author_picture} alt="" className="h-full w-full object-cover" /> : (c.author_name || "?").slice(0, 2).toUpperCase()}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-slate-500"><span className="text-slate-200">{c.author_name}</span> · {ago(c.created_at)}{c.edited_at ? " · edited" : ""}</p>
        {editing ? <Composer initial={c.body} people={people} testId={`comment-edit-${c.id}`} onCancel={() => setEditing(false)} onSubmit={async (b, mentions) => { await act(() => api.put(`/comments/${c.id}`, { body: b, mentions })); setEditing(false); }} />
          : <p className="text-sm text-slate-300 whitespace-pre-wrap break-words mt-0.5" data-testid={`comment-body-${c.id}`}><MentionText body={c.body} mentions={c.mentions} /></p>}
        <div className="flex gap-3 mt-1 text-[11px] text-slate-500">
          {onReply && <button onClick={onReply} className="hover:text-amber-300" data-testid={`comment-reply-${c.id}`}>Reply</button>}
          {!c.parent_id && <button onClick={() => act(() => api.post(`/comments/${c.id}/resolve`, { resolved: !c.resolved }))} className="hover:text-emerald-300" data-testid={`comment-resolve-${c.id}`}>{c.resolved ? "Reopen" : "Resolve"}</button>}
          {c.author_id === me && <button onClick={() => setEditing(true)} className="hover:text-amber-300" data-testid={`comment-editbtn-${c.id}`}><Pencil className="w-3 h-3" /></button>}
          {(c.author_id === me || mod) && <button onClick={() => act(() => api.delete(`/comments/${c.id}`))} className="hover:text-rose-300" data-testid={`comment-delete-${c.id}`}><Trash2 className="w-3 h-3" /></button>}
        </div>
        {replies}
      </div>
    </div>
  );
};

export const Comments = ({ page }) => {
  const { user } = useAuth();
  const { world, reloadCounts } = useWorld();
  const [list, setList] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [open, setOpen] = useState({});
  const [people, setPeople] = useState([]);
  const load = useCallback(() => api.get(`/pages/${page.id}/comments`).then((r) => setList(r.data)), [page.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get(`/worlds/${world.id}/people`).then((r) => setPeople(r.data.filter((p) => p.user_id !== user.id))).catch(() => {}); }, [world.id, user.id]);
  const act = async (fn) => { try { await fn(); await load(); reloadCounts?.(); } catch (e) { toast.error(formatApiError(e)); return false; } };
  const threads = list.filter((c) => !c.parent_id);
  return (
    <section id="comments" className="mt-16 border-t border-line pt-8" data-testid="comments-section">
      <h2 className="font-display text-2xl text-amber-100 mb-5 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-amber-500" /> Comments <span className="text-sm text-slate-500">{list.length}</span></h2>
      {!world.owner_pro && <p className="mb-4 text-xs text-amber-200/80">Comments are paused until the world owner is on Pro again.</p>}
      <div className="space-y-6">
        {threads.map((t) => t.resolved && !open[t.id] ? (
          <button key={t.id} onClick={() => setOpen({ ...open, [t.id]: true })} className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-300" data-testid={`thread-collapsed-${t.id}`}><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Resolved thread by {t.author_name}: "{t.body.slice(0, 60)}"</button>
        ) : (
          <Comment key={t.id} c={t} people={people} me={user.id} mod={isReviewer(world)} act={act} onReply={() => setReplyTo(t.id)}
            replies={<div className="mt-4 space-y-4">
              {list.filter((r) => r.parent_id === t.id).map((r) => <Comment key={r.id} c={r} people={people} me={user.id} mod={isReviewer(world)} act={act} />)}
              {replyTo === t.id && <Composer testId="reply" people={people} placeholder="Write a reply. Type @ to mention someone" onCancel={() => setReplyTo(null)} onSubmit={(b, mentions) => act(() => api.post(`/pages/${page.id}/comments`, { body: b, parent_id: t.id, mentions })).then((r) => { setReplyTo(null); return r; })} />}
            </div>} />
        ))}
      </div>
      {world.owner_pro && <div className="mt-6"><Composer testId="comment" people={people} placeholder="Leave a note for the team. Type @ to mention someone" onSubmit={(b, mentions) => act(() => api.post(`/pages/${page.id}/comments`, { body: b, mentions }))} /></div>}
    </section>
  );
};
